import { type JSX, Show, createMemo, splitProps } from 'solid-js'
import { OptionalBadge } from '../Badge/OptionalBadge'
import { RequiredBadge } from '../Badge/RequiredBadge'
import { EMPTY_VALUE } from '../EmptyValue'
import { FIELD_MESSAGE_METRICS, FieldError } from '../FieldError'
import { ReadOnlyValue } from '../ReadOnlyValue'
import { plainText } from '../ReadOnlyValue/plainText'
import { fieldStateClasses } from './fieldStateClasses'

export type InputSize = 'sm' | 'md' | 'lg'

export interface InputProps extends Omit<JSX.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  size?: InputSize
  error?: string
  label?: string
  description?: string
  hint?: string
  optional?: boolean
  requiredMark?: boolean
  wrapperClass?: string
  appearance?: 'field' | 'plain'
}

const sizeClasses: Record<InputSize, string> = {
  sm: 'h-9 px-3 py-1.5 text-[length:var(--text-sm)] rounded-[var(--radius-12)]',
  md: 'h-10 px-3.5 py-1.5 text-[length:var(--text-base)] rounded-[var(--radius-12)]',
  lg: 'h-12 px-4 py-2.5 text-[length:var(--text-base)] rounded-[var(--radius-16)]',
}

export function Input(props: InputProps) {
  const [local, rest] = splitProps(props, [
    'size',
    'error',
    'label',
    'description',
    'hint',
    'optional',
    'requiredMark',
    'class',
    'id',
    'aria-describedby',
    'readOnly',
    'placeholder',
    'appearance',
    'wrapperClass',
  ])

  const size = () => local.size ?? 'md'
  const readOnly = () => local.readOnly === true
  const placeholderName = () =>
    readOnly() && !local.label && !rest['aria-labelledby'] ? local.placeholder : undefined
  const inputId = createMemo(() => local.id ?? `input-${Math.random().toString(36).slice(2, 9)}`)
  const hintId = () => `${inputId()}-hint`
  const errorId = () => `${inputId()}-error`
  const showsHint = () => Boolean(local.hint) && !local.error
  const describedBy = () =>
    [local.error ? errorId() : null, showsHint() ? hintId() : null, local['aria-describedby']]
      .filter(Boolean)
      .join(' ') || undefined

  return (
    <Show
      when={local.appearance !== 'plain'}
      fallback={
        <ReadOnlyValue
          label={local.label}
          aria-label={rest['aria-label'] ?? local.placeholder}
          value={plainText(rest.value)}
        />
      }
    >
      <div class={local.wrapperClass ?? 'flex flex-col gap-1.5'}>
        <Show when={local.label}>
          <div class="flex flex-col">
            <label
              for={inputId()}
              class="text-[length:var(--text-sm)] font-[var(--font-medium)] text-[color:var(--color-text)]"
            >
              {local.label}
              <Show when={local.optional}>
                <OptionalBadge />
              </Show>
              <Show when={local.requiredMark}>
                <RequiredBadge announced={false} />
              </Show>
            </label>
            <Show when={local.description}>
              <p class="text-[length:var(--text-xs)] text-[color:var(--color-text-muted)] mt-0.5">
                {local.description}
              </p>
            </Show>
          </div>
        </Show>
        <input
          {...rest}
          id={inputId()}
          aria-describedby={describedBy()}
          aria-invalid={local.error ? true : rest['aria-invalid']}
          aria-required={local.requiredMark ? true : rest['aria-required']}
          readOnly={local.readOnly}
          placeholder={readOnly() ? EMPTY_VALUE : local.placeholder}
          aria-label={rest['aria-label'] ?? placeholderName()}
          class={`
          ds-field w-full
          border
          text-[color:var(--color-text)]
          font-[family-name:var(--font-sans)]
          transition-all duration-[var(--duration-normal)] ease-[var(--ease-component)]
          placeholder:text-[color:var(--color-text-subtle)]
          disabled:opacity-50 disabled:cursor-not-allowed
          disabled:bg-[var(--color-background)]
          ${readOnly() ? 'bg-[var(--color-background)] cursor-default' : 'bg-[var(--color-surface)]'}
          ${fieldStateClasses(local.error, readOnly())}
          ${sizeClasses[size()]}
          ${local.class ?? ''}
        `.trim()}
        />
        <Show when={local.error}>
          <FieldError id={errorId()}>{local.error}</FieldError>
        </Show>
        <Show when={showsHint()}>
          <p id={hintId()} class={`${FIELD_MESSAGE_METRICS} text-[color:var(--color-text-muted)]`}>
            {local.hint}
          </p>
        </Show>
      </div>
    </Show>
  )
}
